import React, { useState, useEffect, useRef, useCallback } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { apiFetch } from '../App';
import { Video, VideoOff, Mic, MicOff, Radio, StopCircle, AlertCircle, Loader2 } from 'lucide-react';

AgoraRTC.setLogLevel(3); // Suppress verbose logs

export default function LiveStreamHost({ booking, onStreamStatusChange, onClose }) {
  const [status, setStatus] = useState('idle'); // idle | connecting | live | ended | error
  const [errorMsg, setErrorMsg] = useState('');
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);

  const clientRef = useRef(null);
  const videoTrackRef = useRef(null);
  const audioTrackRef = useRef(null);
  const videoElRef = useRef(null);

  const cleanup = useCallback(async () => {
    if (videoTrackRef.current) {
      videoTrackRef.current.stop();
      videoTrackRef.current.close();
      videoTrackRef.current = null;
    }
    if (audioTrackRef.current) {
      audioTrackRef.current.stop();
      audioTrackRef.current.close();
      audioTrackRef.current = null;
    }
    if (clientRef.current) {
      try {
        if (clientRef.current.connectionState !== 'DISCONNECTED') {
          await clientRef.current.leave();
        }
      } catch {}
    }
  }, []);

  useEffect(() => {
    return () => { cleanup(); };
  }, [cleanup]);

  const startStream = async () => {
    try {
      setStatus('connecting');
      setErrorMsg('');

      // Create Agora client
      clientRef.current = AgoraRTC.createClient({ mode: 'live', codec: 'h264' });
      await clientRef.current.setClientRole('host');

      // Get token from backend
      const tokenData = await apiFetch('/api/agora/token', {
        method: 'POST',
        body: JSON.stringify({ bookingId: booking.id, role: 'host' })
      });

      // Create camera + mic tracks
      videoTrackRef.current = await AgoraRTC.createCameraVideoTrack({
        encoderConfig: '480p_1'
      });
      audioTrackRef.current = await AgoraRTC.createMicrophoneAudioTrack();

      // Audio muted by default
      await audioTrackRef.current.setEnabled(false);

      // Join channel
      await clientRef.current.join(
        tokenData.appId,
        tokenData.channel,
        tokenData.token,
        tokenData.uid
      );

      // Publish both tracks
      await clientRef.current.publish([videoTrackRef.current, audioTrackRef.current]);

      // Play local video preview
      if (videoElRef.current) {
        videoTrackRef.current.play(videoElRef.current);
      }

      // Notify backend that stream has started
      const result = await apiFetch(`/api/bookings/${booking.id}/live/start`, { method: 'POST' });
      setStatus('live');
      if (onStreamStatusChange) onStreamStatusChange('LIVE', result.booking);
    } catch (err) {
      console.error('Start stream error:', err);
      setErrorMsg(err.message || 'Failed to start stream');
      setStatus('error');
      await cleanup();
    }
  };

  const stopStream = async () => {
    try {
      await cleanup();
      await apiFetch(`/api/bookings/${booking.id}/live/stop`, { method: 'POST' });
      setStatus('ended');
      if (onStreamStatusChange) onStreamStatusChange('ENDED');
    } catch (err) {
      console.error('Stop stream error:', err);
      setStatus('ended');
      if (onStreamStatusChange) onStreamStatusChange('ENDED');
    }
  };

  const toggleAudio = async () => {
    if (audioTrackRef.current) {
      const newState = !isAudioEnabled;
      await audioTrackRef.current.setEnabled(newState);
      setIsAudioEnabled(newState);
    }
  };

  const toggleVideo = async () => {
    if (videoTrackRef.current) {
      const newState = !isVideoEnabled;
      await videoTrackRef.current.setEnabled(newState);
      setIsVideoEnabled(newState);
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div className="flex items-center gap-2">
          <Radio size={16} className="text-petfolk-orange" />
          <span className="font-outfit font-bold text-sm" style={{ color: 'var(--text-primary)' }}>Live Grooming Stream</span>
          {status === 'live' && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: '#EF4444', color: 'white' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              LIVE
            </span>
          )}
        </div>
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Booking #{booking.id.slice(0, 8)}</p>
      </div>

      {/* Video Preview */}
      <div className="relative bg-black" style={{ aspectRatio: '16/9', minHeight: 200 }}>
        <div ref={videoElRef} className="w-full h-full" />
        {status === 'idle' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <Video size={40} className="text-white/40" />
            <p className="text-white/60 text-sm">Camera preview will appear here</p>
            <p className="text-white/40 text-xs">Click "Go Live" to start streaming</p>
          </div>
        )}
        {status === 'connecting' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <Loader2 size={36} className="text-petfolk-orange animate-spin" />
            <p className="text-white/70 text-sm">Starting camera...</p>
          </div>
        )}
        {status === 'ended' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <StopCircle size={40} className="text-white/40" />
            <p className="text-white/60 text-sm">Stream ended</p>
          </div>
        )}
        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6">
            <AlertCircle size={36} className="text-red-400" />
            <p className="text-red-300 text-sm text-center">{errorMsg}</p>
            <button
              onClick={() => setStatus('idle')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
              style={{ background: 'var(--petfolk-orange)' }}
            >
              Try Again
            </button>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {status === 'live' && (
            <>
              <button
                onClick={toggleAudio}
                title={isAudioEnabled ? 'Mute mic' : 'Unmute mic'}
                className="p-2 rounded-xl transition-all"
                style={{
                  background: isAudioEnabled ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)',
                  color: isAudioEnabled ? '#22C55E' : '#EF4444'
                }}
              >
                {isAudioEnabled ? <Mic size={16} /> : <MicOff size={16} />}
              </button>
              <button
                onClick={toggleVideo}
                title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
                className="p-2 rounded-xl transition-all"
                style={{
                  background: isVideoEnabled ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.1)',
                  color: isVideoEnabled ? '#22C55E' : '#EF4444'
                }}
              >
                {isVideoEnabled ? <Video size={16} /> : <VideoOff size={16} />}
              </button>
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                {isAudioEnabled ? 'Mic on' : 'Mic muted'}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(status === 'idle' || status === 'error') && (
            <button
              onClick={startStream}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: '#EF4444' }}
            >
              <Radio size={14} />
              Go Live
            </button>
          )}
          {status === 'connecting' && (
            <button disabled className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white opacity-60" style={{ background: '#EF4444' }}>
              <Loader2 size={14} className="animate-spin" />
              Connecting...
            </button>
          )}
          {status === 'live' && (
            <button
              onClick={stopStream}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: '#6B7280' }}
            >
              <StopCircle size={14} />
              End Stream
            </button>
          )}
          {status === 'ended' && (
            <span className="text-sm font-semibold" style={{ color: 'var(--text-secondary)' }}>Stream ended</span>
          )}
        </div>
      </div>
    </div>
  );
}
